import { useCallback, useEffect, useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTeamMemberSummary, MenuProps } from '../../Widgets/WidgetService';
import Authorization from '../../../utils/Authorization';
import TeamMemberSummaryModel from '../../../Models/Widget/TeamMemberSummary.model';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import '../Widgets.css';

const TeamMemberSummary = (props: any) => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const authData = new Authorization();
    const [summary, setSummary] = useState<TeamMemberSummaryModel>(new TeamMemberSummaryModel());
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isDataShow, setIsDataShow] = useState<boolean>(false);
    const [show, setShow] = useState(false);
    const {
        teamMemberSelectedPlans,
        setTeamMemberSelectedPlans,
        teamMemberSelectedTeam,
        setTeamMemberSelectedTeam,
        teamShowInActiveTeams,
        setTeamShowInActiveTeams,
        teamMemberFromDate,
        setTeamMemberFromDate,
        teamMemberToDate,
        setTeamMemberToDate,
    } = useWidgetContext();

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        setShow(!show);
    };

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        setTeamMemberSelectedTeam(authData.getDefaultTeamId(''));
        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, setIsFetched, setTeamMemberSelectedTeam, teamMemberSelectedPlans]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const changeFilter = async () => {
            setIsDataShow(false);
            const data = await fetchTeamMemberSummary(
                teamMemberSelectedPlans.join(','),
                teamMemberSelectedTeam,
                Boolean(teamMemberFromDate) ? new Date(teamMemberFromDate).toISOString() : '',
                Boolean(teamMemberToDate) ? new Date(teamMemberToDate).toISOString() : '',
                teamShowInActiveTeams
            );
            setSummary(data || new TeamMemberSummaryModel());
            setIsDataShow(true);
        };

        if ((teamMemberFromDate != null && teamMemberToDate != null) || (teamMemberFromDate == null && teamMemberToDate == null)) {
            changeFilter();
        }
    }, [teamMemberSelectedPlans, teamMemberSelectedTeam, teamMemberFromDate, teamMemberToDate, teamShowInActiveTeams]);

    const navigateToMembers = (tabIndex?: number) => {
        history(`/provider/metricsummary/teammemberdetail?hasElevatedFilterAccess=${props.hasElevatedFilterAccess}&tab=${tabIndex ?? 0}`);
    };

    const categoryItems = [
        {
            title: 'Open TC - Missing Licensed Visit',
            count: summary.countOpenTcNoLicensedVisit || (summary.scheduledMembersOpenTcNoLicensedVisit ? summary.scheduledMembersOpenTcNoLicensedVisit.length : 0),
            icon: <LocalHospitalIcon color="error" />,
            color: '#ffebee',
            badgeColor: 'error' as const,
            tab: 0,
        },
        {
            title: 'In TC - Pending Discharge',
            count: summary.countInTcNotDischarged || (summary.scheduledMembersInTcNotDischarged ? summary.scheduledMembersInTcNotDischarged.length : 0),
            icon: <CalendarMonthIcon color="warning" />,
            color: '#fff8e1',
            badgeColor: 'warning' as const,
            tab: 1,
        },
        {
            title: 'Expired / Not Met HEDIS Metrics',
            count: summary.countExpiredOrNotMetHedis || (summary.scheduledMembersExpiredOrNotMetHedis ? summary.scheduledMembersExpiredOrNotMetHedis.length : 0),
            icon: <AssessmentIcon color="info" />,
            color: '#e3f2fd',
            badgeColor: 'info' as const,
            tab: 2,
        },
        {
            title: 'Missing Consent or Signed Care Plan',
            count: summary.countMissingConsentOrCarePlan || (summary.scheduledMembersMissingConsentOrCarePlan ? summary.scheduledMembersMissingConsentOrCarePlan.length : 0),
            icon: <DescriptionIcon color="secondary" />,
            color: '#f3e5f5',
            badgeColor: 'secondary' as const,
            tab: 3,
        },
        {
            title: 'Canceled - Needs Reschedule',
            count: summary.countCanceledNeedingReschedule || (summary.scheduledMembersCanceledNeedingReschedule ? summary.scheduledMembersCanceledNeedingReschedule.length : 0),
            icon: <EventBusyIcon color="error" />,
            color: '#efebe9',
            badgeColor: 'default' as const,
            tab: 4,
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto', p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                                <span className="pointer" onClick={() => navigateToMembers(0)}>
                                    Team Schedule & Clinical Needs (7-Day)
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">
                                                Examines scheduled members for the next 7 days and identifies open TC without licensed visits, pending TC discharges, expired HEDIS metrics, missing consents/care plans, and canceled appointments needing rescheduling.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                            <Chip
                                label={`Ref Date: ${new Date(summary.referenceDate || new Date()).toLocaleDateString('en-US')}`}
                                variant="outlined"
                                color="default"
                                size="small"
                                sx={{ ml: 1, fontWeight: 'bold' }}
                            />
                            {summary.totalScheduledMembersNext7Days > 0 && (
                                <Chip
                                    label={`${summary.totalScheduledMembersNext7Days} Members Scheduled (Next 7 Days)`}
                                    color="primary"
                                    size="small"
                                    sx={{ ml: 1, fontWeight: 'bold' }}
                                />
                            )}
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={3.5}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={teamMemberSelectedTeam}
                                    onChange={(id) => setTeamMemberSelectedTeam(id)}
                                    showInactive={teamShowInActiveTeams}
                                    onToggleInactive={setTeamShowInActiveTeams}
                                />
                            </Grid>
                        )}
                        <Grid size={3.5}>
                            <HealthPlanMultiSelect
                                value={teamMemberSelectedPlans}
                                onChange={setTeamMemberSelectedPlans}
                                menuProps={MenuProps}
                            />
                        </Grid>
                        <Grid size={2.5}>
                            <DesktopDatePicker
                                label="From Date"
                                value={Boolean(teamMemberFromDate) ? teamMemberFromDate : null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => setTeamMemberFromDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            />
                        </Grid>
                        <Grid size={2.5}>
                            <DesktopDatePicker
                                label="To Date"
                                value={Boolean(teamMemberToDate) ? teamMemberToDate : null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => setTeamMemberToDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            />
                        </Grid>

                        <Grid size={12} marginTop={1} sx={{ position: 'relative' }}>
                            {!isDataShow ? (
                                <Box className="loader-center" sx={{ height: 'calc(100vh - 285px)', maxHeight: '400px' }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer component={Paper} sx={{ maxHeight: '420px' }}>
                                    <Table aria-label="team summary table" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell size="small">Scheduled Member Need Category</TableCell>
                                                <TableCell align="right" size="small">Count</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {categoryItems.map((item) => (
                                                <TableRow
                                                    key={item.title}
                                                    hover
                                                    className="pointer"
                                                    onClick={() => navigateToMembers(item.tab)}
                                                    sx={{ backgroundColor: item.count > 0 ? item.color : 'inherit' }}
                                                >
                                                    <TableCell size="small" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {item.icon}
                                                        <Typography variant="body2" fontWeight={item.count > 0 ? 'bold' : 'normal'}>
                                                            {item.title}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" size="small">
                                                        <Chip
                                                            label={item.count}
                                                            color={item.badgeColor}
                                                            size="small"
                                                            sx={{ fontWeight: 'bold', minWidth: '40px' }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default TeamMemberSummary;
